import { Shape } from '@/services/shape';
import { ImageArrayAttribute } from '@/services/shape/attributes/image-array';
import {
  addResourceIfNotExists,
  generateLayoutAst,
  getImageArrayWidgetMaxSize,
  insertImages
} from '../../utils/resource.utils';
import { DataSourceFormat } from '@/resources/enum';
import { generateResourceName, getRefName } from '../../utils/name.utils';
import generateID from '@/utils/generate-id';
import { ResourceKey } from '@/services/shape/attributes';
import { buildAstState } from '../..';
import { canUse } from '@/utils/can-use.utils';
import { getImageArrayAstLen } from '../../utils/image-array.utils';
import { cloneDeep } from 'lodash-es';
import {
  getCompassDirectionsArr,
  isN65ABWeatherPrecision
} from '@/hooks/use-image-array';
import { AQI_AQL } from '../../utils/data-source.utils';
import { getWeatherAstIndexs } from '../../utils/weather.utils';

const _repalceAQLByAQI = (ast: Ast) => {
  ast.attrsMap.source = 'weatherCurrentAirQualityIndex';
  const values = AQI_AQL.map((item) => item.value);
  values.forEach((val) => {
    ast.children.push({
      tag: 'Param',
      attrsMap: {
        value: String(val)
      },
      children: []
    });
  });
};
const _chineseTime = (ast: Ast) => {
  ast.attrsMap.source = 'timeHour24H';
  let i = 0;
  while (i <= 23) {
    ast.children.push({
      tag: 'Param',
      attrsMap: {
        value: String(i)
      },
      children: []
    });
    i = i == 0 ? i + 1 : i + 2;
  }
};

// 将指南针方向转换为指南针角度
const _systemSensorCompassDirection = (ast: Ast, needEmptyValue: boolean) => {
  ast.attrsMap.source = 'systemSensorCompass';
  getCompassDirectionsArr(true, needEmptyValue).forEach((item) => {
    ast.children.push({
      tag: 'Param',
      attrsMap: {
        value: String(item.value)
      },
      children: []
    });
  });
};

const _customProgressAccuracy = (ast: Ast, data: number[][]) => {
  const values: number[] = [];
  data.forEach((item) => {
    const [a] = item;
    values.push(a);
  });
  values.sort((a, b) => a - b);
  for (let i = 0; i < values.length; i++) {
    const val = values[i];
    ast.children.push({
      tag: 'Param',
      attrsMap: {
        value: String(val)
      },
      children: []
    });
  }
};

const _getImageArrayAst = (
  resourceAst: Ast,
  shape: Shape,
  imageMap: { [resourceName in ResourceKey]?: string },
  sourceItem: DataSourceItem | undefined,
  unitIcon: ResourceKey,
  // 数字填充下的renderRule
  renderRule: 'hideWhenUnitMismatch' | 'alwaysShow'
) => {
  const attrs = shape.attrs as ImageArrayAttribute;
  const {
    fillType,
    rotation = 0,
    recolorEnable,
    sourceWeatherType = 'whole',
    sourceHourMode = 'system',
    trailingZero = false,
    sourceProgressCustom,
    compassFps = 50
  } = attrs;
  const deviceType = buildAstState.data.description?.deviceType ?? 'N62';

  // 添加上图片数字或图片数值对
  const tag =
    fillType == '普通填充' ? 'DataItemImageValues' : 'DataItemImageNumber';
  const sourceKey = sourceItem?.key ?? '';
  const resourceItem: Ast = {
    tag,
    attrsMap: {
      source:
        sourceKey == 'timeHour'
          ? sourceHourMode == '24h'
            ? 'timeHour24H'
            : sourceHourMode == '12h'
            ? 'timeHour12H'
            : 'timeHour'
          : sourceKey,
      ref: getRefName(imageMap['images']),
      rotation: String(rotation),
      supportRecolor: String(recolorEnable ?? false)
    },
    children: []
  };

  // 如果是图片数字
  if (attrs.fillType == '数字填充') {
    // 多单位只在数字填充下生效
    const { sourceFormat } = shape.attrs;
    // 小数所占用的位数
    const precision = sourceItem?.precision
      ? sourceItem?.precision
      : sourceItem && isN65ABWeatherPrecision(sourceItem, deviceType)
      ? 1
      : 0;
    // 用户设置的小数位数
    const _decimalDigits = attrs.decimalDigits ?? precision;
    const decimalDigits = precision
      ? Math.max(Math.min(precision, _decimalDigits), 0)
      : 0;

    // 计算所占位数
    const minValue = sourceItem?.minValue;
    const maxValue = sourceItem?.maxValue;
    const totalDigits =
      Math.max(String(maxValue ?? 0).length, String(minValue ?? 0).length) +
      (decimalDigits > 0 ? decimalDigits + 1 : 0);

    let _renderRule =
      renderRule == 'hideWhenUnitMismatch'
        ? 'hideWhenUnitMismatch'
        : 'alwaysShow';

    // 判断是来着1.5表盘且为温度类型图组，需要用1.5的renderRule
    // 1.5中天气没有使用组合类型，是什么数据源就是什么数据源
    const sourceKey = attrs.source ?? '';
    if (
      // 是否温度类数据源
      sourceKey.includes('Temperature') &&
      !sourceKey.endsWith('_temp') &&
      // 排除体感温度数据源
      sourceKey != 'weatherCurrentTemperatureFeel'
    ) {
      _renderRule = shape.attrs['renderRule' as 'name'] ?? _renderRule;
    }

    const attrsMap = {
      unitIcon: [
        DataSourceFormat.Format_8时,
        DataSourceFormat.Format_08时,
        DataSourceFormat['Format_8.0时']
      ].includes(sourceFormat as 1)
        ? getRefName(imageMap[unitIcon])
        : '',
      leadingZero: [
        DataSourceFormat.Format_08,
        DataSourceFormat.Format_08时
      ].includes(sourceFormat as 2)
        ? 'true'
        : 'false',
      decimalOffsetX: shape.attrs.decimalOffsetX ?? '0',
      totalDigits,
      decimalDigits,
      trailingZero: decimalDigits ? String(trailingZero) : 'false',
      parameter: '1000',
      renderRule: _renderRule,
      align: attrs.align ?? 'left',
      space: attrs.space ?? '0'
    };
    if (
      deviceType === 'O61' &&
      sourceKey == 'systemSensorCompass' &&
      compassFps
    ) {
      attrsMap['parameter'] = String(Math.round(1000 / compassFps));
    }

    Object.assign(resourceItem.attrsMap, attrsMap);
  }

  if (attrs.fillType == '普通填充') {
    const {
      minValue: _minValue = 0,
      maxValue: _maxValue = 0,
      progressAccuracy,
      needEmptyValue
    } = sourceItem ?? {};
    const isNeedEmpty = needEmptyValue
      ? needEmptyValue(deviceType, fillType)
      : false;

    let minValue = _minValue;
    let maxValue = _maxValue;
    if (sourceKey == 'timeHour') {
      if (sourceHourMode == '12h') {
        minValue = 1;
        maxValue = 12;
      }
    }
    // 普通填充
    // 判断是否需要为每个图片指定值
    // 天气类
    // const weatherRange = weatherValueRange?.[sourceWeatherType ?? 'whole'];
    if (sourceItem?.key == 'weatherCurrentWeather') {
      const imageArrayIndexs = getWeatherAstIndexs({
        type: sourceWeatherType,
        deviceType,
        fullWeatherCode: buildAstState.options.buildFor == 'editor'
      });

      imageArrayIndexs.forEach((item) => {
        resourceItem.children.push({
          tag: 'Param',
          attrsMap: {
            value: String(item.value),
            _label: item.label ?? ''
          },
          children: []
        });
      });
    } else if (progressAccuracy?.length) {
      // 进度类型
      const { sourceProgressAccuracy = 10 } = attrs;
      // 电量 普通填充
      let start = minValue;
      const batterySpecialCut = canUse('battery-special-cut', deviceType).valid;
      if (
        sourceKey === 'systemStatusBattery' &&
        sourceProgressAccuracy === 10 &&
        batterySpecialCut
      ) {
        while (start <= maxValue) {
          resourceItem.children.push({
            tag: 'Param',
            attrsMap: {
              value: String(start)
            },
            children: []
          });
          if (start === 0) start = 11;
          else start += sourceProgressAccuracy;
        }
        resourceItem.children.push({
          tag: 'Param',
          attrsMap: {
            value: '100'
          },
          children: []
        });
      } else {
        if (sourceProgressAccuracy) {
          while (start <= maxValue) {
            resourceItem.children.push({
              tag: 'Param',
              attrsMap: {
                value: String(start)
              },
              children: []
            });
            start += sourceProgressAccuracy;
          }
        } else {
          // 自定义进度
          if (sourceProgressCustom?.length) {
            _customProgressAccuracy(
              resourceItem,
              cloneDeep(sourceProgressCustom)
            );
          }
        }
      }
    } else if (sourceKey == 'weatherCurrentAirQualityLevel') {
      // 老设备通过 AQI 阈值转换
      _repalceAQLByAQI(resourceItem);
    } else if (sourceKey == 'weatherAirQualityLevel') {
      // 新设备固件直接下发等级值 0-5
      for (let i = minValue; i <= maxValue; i++) {
        resourceItem.children.push({
          tag: 'Param',
          attrsMap: { value: String(i) },
          children: []
        });
      }
    } else if (sourceKey == 'timeChineseTime') {
      _chineseTime(resourceItem);
    } else if (sourceKey == 'systemSensorCompassDirection') {
      _systemSensorCompassDirection(resourceItem, isNeedEmpty);
    } else if (minValue > 0) {
      // 如果数据源不是从0开始
      // 此项需要设置在最后
      let i = minValue;
      const max = maxValue;
      while (i <= max) {
        resourceItem.children.push({
          tag: 'Param',
          attrsMap: {
            value: String(i)
          },
          children: []
        });
        i++;
      }
    }

    // 是否存在无效值
    if (buildAstState.data.imageArrayEmptyEnabled && isNeedEmpty) {
      resourceItem.attrsMap['hasInvalidValueImage'] = 'true';
      if (resourceItem.children?.length) {
        resourceItem.children.push({
          tag: 'Param',
          attrsMap: {
            value: 'INT_MAX'
          },
          children: []
        });
      }
    }
  }

  const ast = addResourceIfNotExists(resourceAst, resourceItem);
  if (
    deviceType === 'O61' &&
    sourceKey == 'systemSensorCompass' &&
    compassFps
  ) {
    ast.attrsMap['parameter'] = String(Math.round(1000 / compassFps));
  }

  // 生成 layout ast
  const layoutAst: Ast = generateLayoutAst({
    ref: '@' + ast.attrsMap.name,
    x: attrs.x,
    y: attrs.y
  });

  return layoutAst;
};

const _prehandleResources = (shape: Shape) => {
  const attrs = shape.attrs as ImageArrayAttribute;
  const { resources: _resources, sourceWeatherType = 'whole' } = attrs;

  // 添加图片
  let resources = cloneDeep(_resources);
  // 如果是天气，则需要提前处理资源
  // const weatherRange = shape.sourceItem?.weatherValueRange?.[sourceWeatherType];
  if (shape.sourceItem?.key == 'weatherCurrentWeather') {
    const images = _resources['images'] ?? {};
    resources = {
      images: {}
    };
    const colorIds = Object.keys(images);
    colorIds.forEach((colorId) => {
      const imageArrayIndexs = getWeatherAstIndexs({
        type: sourceWeatherType,
        deviceType: buildAstState.data.description?.deviceType ?? 'N62',
        fullWeatherCode: buildAstState.options.buildFor == 'editor'
      });
      const srcArr2: {
        src: string;
        value: number;
      }[] = imageArrayIndexs.map((item) => {
        const src = images[colorId]?.[item.imageIndex ?? -1] ?? '';
        if (!src) {
          console.log(item);
        }
        return {
          src,
          value: item.value
        };
      });

      if (!resources['images']) resources['images'] = {};
      resources['images'][colorId] = srcArr2.map((item) => item.src);
    });
  }

  // 如果是时辰
  if (shape.sourceItem?.key == 'timeChineseTime') {
    const images = _resources['images'] ?? {};
    const colorIds = Object.keys(images);
    colorIds.forEach((colorId) => {
      if (!resources['images']) resources['images'] = {};
      resources['images'][colorId].push(resources['images'][colorId]?.[0]);
    });
  }

  // 如果是指南针方向
  if (shape.sourceItem?.key == 'systemSensorCompassDirection') {
    const needEmptyValue = shape.sourceItem?.needEmptyValue
      ? shape.sourceItem?.needEmptyValue(
          buildAstState.data.description?.deviceType
        )
      : false;
    const images = _resources['images'] ?? {};
    const colorIds = Object.keys(images);
    colorIds.forEach((colorId) => {
      const arr = resources['images']?.[colorId];
      if (!arr?.length) return;
      if (!needEmptyValue) {
        arr.push(arr[0]);
      } else {
        arr.splice(arr.length - 1, 0, arr[0]);
      }
    });
  }

  // 数字填充：有小数点但没有负号/空状态时，确保 index 10 位置有图片
  // 固件端要求完整12张图片，占位位置为空时用第一张图片填充
  if (attrs.fillType == '数字填充') {
    const deviceType = buildAstState.data.description?.deviceType ?? 'N62';
    const includePrecision = shape.sourceItems.some((item) => {
      if (item && isN65ABWeatherPrecision(item, deviceType)) return true;
      return !!item?.precision;
    });
    if (includePrecision) {
      const min = Math.min(
        ...shape.sourceItems.map((item) => item?.minValue ?? 0)
      );
      const isSupportEmpty = canUse(
        'shape-image-array-empty',
        deviceType
      ).valid;
      const needEmptyValue = shape.sourceItem?.needEmptyValue
        ? shape.sourceItem?.needEmptyValue(deviceType, attrs.fillType)
        : false;
      const hasInvalidValueImage = isSupportEmpty && needEmptyValue;
      if (!hasInvalidValueImage && min >= 0) {
        const images = resources['images'] ?? {};
        const colorIds = Object.keys(images);
        colorIds.forEach((colorId) => {
          const arr = resources['images']?.[colorId];
          if (!arr?.length) return;
          // 占位位置（index 10）为空时，用第一张图片填充
          if (!arr[10]?.trim() && arr[0]) {
            arr[10] = arr[0];
          }
        });
      }
    }
  }

  return resources;
};

export const getImageArrayAst = (resourceAst: Ast, shape: Shape) => {
  const attrs = shape.attrs as ImageArrayAttribute;
  const useForEditor = buildAstState.options.buildFor == 'editor';
  const {
    recolorEnable,
    sourceFormat,
    format,
    space = 0,
    scale = 1,
    alpha = 1
  } = attrs;

  // 全局缩放 × 元素自身缩放
  const themeScale = buildAstState.data?.theme?.scale ?? 1;
  const effectiveScale = themeScale * scale;

  const resources = _prehandleResources(shape);
  const imageMap = insertImages(resourceAst, resources, {
    recolorEnable: recolorEnable,
    imgFormat: format,
    imageArrayLength: getImageArrayAstLen(
      shape,
      buildAstState.data.description?.deviceType ?? 'N62',
      useForEditor
    ),
    scale: effectiveScale,
    alpha
  });

  // 当需要单位在前面时：目前只有紫外线指数数据源会出现这种情况
  if ([DataSourceFormat.Format_SPF8].includes(sourceFormat as 1)) {
    // 单位在前面需要特别处理
    const layoutAst = _getImageArrayAst(
      resourceAst,
      shape,
      imageMap,
      shape.sourceItem,
      'unitIcon',
      'alwaysShow'
    );
    // 插入uniticon图片
    const _imageUnitIcon = () => {
      const layoutAst: Ast = generateLayoutAst(
        {
          ref: getRefName(imageMap['unitIcon']),
          x: 0,
          y: 0
        },
        'Item'
      );
      return layoutAst;
    };
    const children = [_imageUnitIcon(), layoutAst];
    children.forEach((item) => {
      item.tag = 'Item';
      item.attrsMap._id = generateID();
    });
    // 获取大小
    const { width, height } = getImageArrayWidgetMaxSize({
      resources,
      gap: space,
      space,
      format: 'SPF08'
    });
    const widgetAst: Ast = {
      tag: 'Widget',
      attrsMap: {
        name: generateResourceName(resourceAst, 'Widget'),
        flex_direction: 'row',
        justify_content: 'flex-start',
        align_items: 'flex-start',
        gap: String(space),
        w: String(width),
        h: String(height)
      },
      children
    };

    const _ast = addResourceIfNotExists(resourceAst, widgetAst);
    const layoutAst2: Ast = generateLayoutAst({
      ref: '@' + _ast.attrsMap.name,
      x: attrs.x,
      y: attrs.y
    });
    return layoutAst2;
  } else {
    const layouts: Ast[] = [];
    const isMultiUnit = shape.sourceItems?.length > 1;

    shape.sourceItems.forEach((sourceItem, index) => {
      const layoutAst = _getImageArrayAst(
        resourceAst,
        shape,
        imageMap,
        sourceItem,
        index > 0 ? 'unitIcon2' : 'unitIcon',
        isMultiUnit ? 'hideWhenUnitMismatch' : 'alwaysShow'
      );
      layouts.push(layoutAst);
    });
    return layouts;
  }
};
